import { Block } from '@/shared/ui/blocks/block'
import { Button } from '@/shared/ui/button'
import { VLayout } from '@/shared/ui/layouts/flex'
import { VendomeLayout } from '@/shared/ui/layouts/vendome'
import { Typography } from '@/shared/ui/typography'

import type { FC } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * ErrorScreen - Full-screen error state, built with Design System components.
 *
 * Serves two paths: the router catch-all (unknown URL) and the authentication boot failure in
 * `auth.provider.tsx`. It carries no data, no state, and exposes a single action — a link back
 * to the home page.
 *
 * Design System migration of the legacy error view (since removed).
 *
 * Named `ErrorScreen` rather than `Error` on purpose: `Error` would shadow the global constructor
 * here and in every consumer.
 */
export const ErrorScreen: FC = () => {
  const { t } = useTranslation()

  return (
    <VendomeLayout>
      <VLayout items="center" justify="center">
        {/*
         * The `main` landmark keeps parity with the legacy screen. It sits OUTSIDE the Block so it
         * stays clear of the accordion `role="region"` Radix emits inside.
         *
         * Width: Block has no width prop and shrinks to its content, which leaves the card too
         * narrow. `max-w-96` (384px) is a set width chosen for this screen (the login card, the
         * only other app-shell surface with one, is 448px: a 368px content column plus 40px of
         * padding); `w-full` lets it shrink below that on narrow viewports, which matters because
         * VendomeLayout is `overflow-hidden` — a fixed width would clip the card with no way to
         * scroll to it.
         */}
        <main className="w-full max-w-96">
          {/*
           * A static Block is used as a plain white surface: `collapsible={false}` drops the chevron
           * and the collapse behaviour, `contentVariant="soft"` paints the content `base-100` (#fff).
           * Standalone usage is supported — Block auto-wraps itself in a Block.List.
           */}
          <Block collapsible={false} contentVariant="soft">
            <Block.Content>
              {/*
               * `.block-content--soft` forces `pt-0` because Block.Content is designed to sit under
               * a Block.Title, which supplies the top padding. This screen has no header, so the top
               * padding is restored here to keep the card symmetric. Raised as a Design System
               * backlog item — a standalone surface variant would remove the need for this wrapper.
               */}
              <div className="pt-5">
                <VLayout items="center" gap="8">
                  {/*
                   * Decorative code. `neutral-500` replaces the legacy `base-300`: at this type
                   * scale the legacy colour lands around 1.2:1 on white, i.e. invisible.
                   */}
                  <Typography type="heading" variant="h1-bold" color="neutral-500">
                    404
                  </Typography>

                  <Typography type="heading" variant="h4-bold">
                    {t('app.error.notFoundTitle')}
                  </Typography>

                  <Button intent="secondary" variant="outline" size="lg" to="/">
                    {t('common.returnToHome')}
                  </Button>
                </VLayout>
              </div>
            </Block.Content>
          </Block>
        </main>
      </VLayout>
    </VendomeLayout>
  )
}
